import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Search, Filter, FileText, Pill, Calendar, 
  User, ChevronLeft, ChevronRight, X, Eye,
  RefreshCw, Download
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import { debounce } from 'lodash';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'all', label: 'Todos os tipos' },
  { value: 'receita', label: 'Receitas' },
  { value: 'atestado', label: 'Atestados' },
  { value: 'exame', label: 'Pedidos de Exame' },
  { value: 'documento', label: 'Outros Documentos' }
];

export default function DocumentSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [showFilters, setShowFilters] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Filtros
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [tipo, setTipo] = useState(searchParams.get('tipo') || 'all');
  const [dataInicio, setDataInicio] = useState(searchParams.get('data_inicio') || '');
  const [dataFim, setDataFim] = useState(searchParams.get('data_fim') || '');
  const [medicamento, setMedicamento] = useState(searchParams.get('medicamento') || '');
  const [cpf, setCpf] = useState(searchParams.get('cpf') || '');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // Buscar documentos
  const searchDocuments = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (tipo && tipo !== 'all') params.append('tipo', tipo);
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);
      if (medicamento) params.append('medicamento', medicamento);
      if (cpf) params.append('cpf', cpf);
      params.append('page', page.toString());
      params.append('limit', '20');

      const response = await axios.get(
        `${API}/search/documents?${params.toString()}`,
        getAuthHeaders()
      );

      setResults(response.data.results || []);
      setTotalResults(response.data.total || 0);
      setCurrentPage(response.data.page || 1);
      setTotalPages(response.data.pages || 1);

      // Update URL params
      setSearchParams(params);
    } catch (error) {
      console.error('Erro na busca:', error);
      toast.error('Erro ao buscar documentos');
    } finally {
      setLoading(false);
    }
  }, [query, tipo, dataInicio, dataFim, medicamento, cpf, setSearchParams]);

  // Autocomplete
  const fetchSuggestions = useCallback(
    debounce(async (term) => {
      if (term.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/search/documents/autocomplete?q=${encodeURIComponent(term)}`,
          getAuthHeaders()
        );
        setSuggestions(response.data.suggestions || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Erro no autocomplete:', error);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (query) {
      fetchSuggestions(query);
    } else {
      setSuggestions([]);
    }
  }, [query, fetchSuggestions]);

  // Busca inicial baseada nos params da URL
  useEffect(() => {
    if (searchParams.toString()) {
      searchDocuments(1);
    }
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    searchDocuments(1);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.tipo === 'paciente') {
      setQuery(suggestion.valor);
    } else if (suggestion.tipo === 'medicamento') {
      setMedicamento(suggestion.valor);
    }
    setShowSuggestions(false);
    searchDocuments(1);
  };

  const clearFilters = () => {
    setQuery('');
    setTipo('all');
    setDataInicio('');
    setDataFim('');
    setMedicamento('');
    setCpf('');
    setResults([]);
    setSearchParams({});
  };

  const getDocumentIcon = (tipo) => {
    switch (tipo) {
      case 'receita':
        return <Pill className="w-4 h-4 text-blue-600" />;
      case 'exame':
        return <FileText className="w-4 h-4 text-purple-600" />;
      case 'atestado':
        return <FileText className="w-4 h-4 text-green-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getDocumentLabel = (tipo) => {
    switch (tipo) {
      case 'receita':
        return 'Receita';
      case 'exame':
        return 'Pedido de Exame';
      case 'atestado':
        return 'Atestado';
      case 'declaracao':
        return 'Declaração';
      case 'laudo':
        return 'Laudo';
      default:
        return 'Documento';
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F8]">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1F2933] flex items-center gap-3">
            <Search className="w-7 h-7 text-[#0F766E]" />
            Busca de Documentos
          </h1>
          <p className="text-[#64748B] mt-1">
            Pesquise receitas, atestados, pedidos de exame e outros documentos
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6 mb-6">
          <form onSubmit={handleSearch}>
            {/* Main Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por paciente, medicamento, CPF..."
                className="pl-12 pr-24 h-12 text-lg"
                data-testid="search-input"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#0F766E] hover:bg-[#0D6660]"
                disabled={loading}
                data-testid="search-submit-button"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Buscar'}
              </Button>

              {/* Autocomplete Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#D1D5DB] rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {suggestions.map((s, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSuggestionClick(s)}
                      className="w-full px-4 py-3 text-left hover:bg-[#F4F7F8] flex items-center gap-3"
                    >
                      {s.tipo === 'paciente' ? (
                        <User className="w-4 h-4 text-[#0F766E]" />
                      ) : (
                        <Pill className="w-4 h-4 text-blue-600" />
                      )}
                      <div>
                        <div className="font-medium text-[#1F2933]">{s.texto}</div>
                        <div className="text-xs text-[#64748B]">{s.subtexto}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              </Button>

              {(query || tipo !== 'all' || dataInicio || dataFim || medicamento || cpf) && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4 mr-2" />
                  Limpar Filtros
                </Button>
              )}
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-[#D1D5DB] grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="tipo">Tipo de Documento</Label>
                  <select
                    id="tipo"
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-[#D1D5DB] bg-white"
                    data-testid="filter-tipo"
                  >
                    {DOCUMENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="dataInicio">Data Inicial</Label>
                  <Input
                    id="dataInicio"
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    data-testid="filter-data-inicio"
                  />
                </div>

                <div>
                  <Label htmlFor="dataFim">Data Final</Label>
                  <Input
                    id="dataFim"
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    data-testid="filter-data-fim"
                  />
                </div>

                <div>
                  <Label htmlFor="medicamento">Medicamento/Princípio Ativo</Label>
                  <Input
                    id="medicamento"
                    value={medicamento}
                    onChange={(e) => setMedicamento(e.target.value)}
                    placeholder="Ex: Losartana"
                    data-testid="filter-medicamento"
                  />
                </div>

                <div>
                  <Label htmlFor="cpf">CPF do Paciente</Label>
                  <Input
                    id="cpf"
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                    placeholder="000.000.000-00"
                    data-testid="filter-cpf"
                  />
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Results */}
        <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB]">
          {/* Results Header */}
          <div className="p-4 border-b border-[#D1D5DB] flex items-center justify-between">
            <div>
              <span className="font-medium text-[#1F2933]">{totalResults}</span>
              <span className="text-[#64748B]"> resultados encontrados</span>
            </div>
            {totalResults > 0 && (
              <div className="text-sm text-[#64748B]">
                Página {currentPage} de {totalPages}
              </div>
            )}
          </div>

          {/* Results List */}
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#0F766E] mb-2" />
              <p className="text-[#64748B]">Buscando...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-[#64748B]">
              {query || tipo !== 'all' || medicamento || cpf 
                ? 'Nenhum documento encontrado com os filtros aplicados.'
                : 'Digite algo para buscar documentos.'}
            </div>
          ) : (
            <div className="divide-y divide-[#F4F7F8]">
              {results.map((doc, idx) => (
                <div key={doc.id || idx} className="p-4 hover:bg-[#F4F7F8] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#F4F7F8] p-2 rounded-lg">
                        {getDocumentIcon(doc.tipo)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-[#1F2933]">
                            {doc.paciente_nome || 'Paciente'}
                          </span>
                          <span className="px-2 py-0.5 bg-[#F4F7F8] rounded text-xs text-[#64748B]">
                            {getDocumentLabel(doc.tipo)}
                          </span>
                        </div>
                        
                        {doc.paciente_cpf && (
                          <p className="text-sm text-[#64748B]">CPF: {doc.paciente_cpf}</p>
                        )}
                        
                        {doc.medicamentos && doc.medicamentos.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {doc.medicamentos.slice(0, 5).map((med, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                {med}
                              </span>
                            ))}
                            {doc.medicamentos.length > 5 && (
                              <span className="text-xs text-[#64748B]">
                                +{doc.medicamentos.length - 5} mais
                              </span>
                            )}
                          </div>
                        )}

                        {doc.principios_ativos && doc.principios_ativos.length > 0 && (
                          <div className="mt-1 text-xs text-[#64748B]">
                            Princípios ativos: {doc.principios_ativos.slice(0, 3).join(', ')}
                          </div>
                        )}

                        {doc.exames && doc.exames.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {doc.exames.slice(0, 3).map((exam, i) => (
                              <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                {exam}
                              </span>
                            ))}
                          </div>
                        )}

                        {doc.titulo && (
                          <p className="text-sm text-[#64748B] mt-1">{doc.titulo}</p>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm text-[#64748B] mb-2">
                        <Calendar className="w-4 h-4" />
                        {doc.data ? new Date(doc.data).toLocaleDateString('pt-BR') : '-'}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (doc.patient_id) {
                            navigate(`/paciente/${doc.patient_id}/historico`);
                          }
                        }}
                        data-testid={`view-doc-${doc.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-[#D1D5DB] flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => searchDocuments(currentPage - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => searchDocuments(pageNum)}
                    className={currentPage === pageNum ? "bg-[#0F766E]" : ""}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => searchDocuments(currentPage + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
